import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk'

export type UseFeatureFlagsLoginParams = {
  /**
   * The identifying key that identifies a user in our systems, such as a user ID.
   */
  key: string
  /**
   * The user's primary email address.
   */
  email: string
  /**
   * The company id of the user's employer.
   */
  companyId: string
}

export type UseFeatureFlags = {
  /**
   * An object containing all feature flags used by the application.
   */
  flags: {
    /**
     * Controls visibility of the QLE v1 feature.
     */
    ['qle-v-1']: boolean
    /**
     * Controls visibility of the Zendesk widget.
     */
    ['customer-service.fe.zendesk-widget-enabled']: boolean
    /**
     * Controls visibility of Renewals feature
     */
    ['product.enrollme.renewals']: boolean
    /**
     * Controls visibility of custom split family feature
     */
    ['product.enrollme.custom.split.family']: boolean
    /**
     * Controls visibility of professional level inputs
     */
    ['product.enrollme.professional-experience-inputs']: boolean
    /**
     * Contains the time when the enrollment period ends. (tech debt)
     */
    ['product.enrollme.enrollment-cutoff-time']: string
  }
  /**
   * Callback methods used to perform actions on our feature flag storage, such as identifying a user after login.
   */
  methods: {
    /**
     * Identifies a user after a successful login.
     */
    identify: (params: UseFeatureFlagsLoginParams) => void
  }
}

export const useFeatureFlags = (): UseFeatureFlags => {
  const flags = useFlags()
  const ldClient = useLDClient()

  const identify = ({ key, email, companyId }: UseFeatureFlagsLoginParams) => {
    ldClient.identify({ key, email, custom: { companyId } })
  }

  return { flags, methods: { identify } } as UseFeatureFlags
}

export default useFeatureFlags
